import { useMemo, useState, type FormEvent } from 'react';
import {
  ShieldOff, Plus, Search, Trash2, AlertCircle, UserX, AlertTriangle, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useSuppressions, useAddSuppression, useRemoveSuppression,
} from '@/hooks/useNewsletter';
import type { Suppression, SuppressionReason } from '@/types/newsletter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';

const REASON_LABELS: Record<SuppressionReason, string> = {
  unsubscribe:   'Unsubscribed',
  hard_bounce:   'Hard Bounce',
  complaint:     'Spam Complaint',
  manual:        'Manual',
  list_cleaning: 'List Cleaning',
};

const REASON_TONES: Record<SuppressionReason, 'neutral' | 'danger' | 'warning'> = {
  unsubscribe:   'neutral',
  hard_bounce:   'danger',
  complaint:     'danger',
  manual:        'warning',
  list_cleaning: 'neutral',
};

const REASON_ICONS: Record<SuppressionReason, typeof UserX> = {
  unsubscribe:   UserX,
  hard_bounce:   AlertTriangle,
  complaint:     Ban,
  manual:        UserX,
  list_cleaning: UserX,
};

export function SuppressionsPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const { data, isLoading, error } = useSuppressions();
  const add = useAddSuppression();
  const remove = useRemoveSuppression();

  const [search, setSearch] = useState('');
  const [reason, setReason] = useState<'' | SuppressionReason>('');
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [removing, setRemoving] = useState<Suppression | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((s) => {
      if (reason && s.reason !== reason) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (s.email + ' ' + (s.notes ?? '')).toLowerCase().includes(q);
      }
      return true;
    });
  }, [data, search, reason]);

  const counts = useMemo(() => {
    if (!data) return { total: 0, unsubscribe: 0, bounce: 0, complaint: 0 };
    return {
      total: data.length,
      unsubscribe: data.filter((s) => s.reason === 'unsubscribe').length,
      bounce: data.filter((s) => s.reason === 'hard_bounce').length,
      complaint: data.filter((s) => s.reason === 'complaint').length,
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="Could not load suppressions"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'} />
      </Card>
    );
  }

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await add.mutateAsync({ email: newEmail.trim().toLowerCase(), notes: newNotes });
      toast.success(`Suppressed ${newEmail}`);
      setNewEmail(''); setNewNotes(''); setAddOpen(false);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to add.');
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    try {
      await remove.mutateAsync(removing.id);
      toast.success(`Removed ${removing.email} from suppressions`);
      setRemoving(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Remove failed.');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="kicker mb-1">Suppression List</div>
        <p className="text-sm text-text-muted max-w-2xl">
          Emails on this list will never be sent to. Entries are added automatically by unsubscribes,
          hard bounces, and spam complaints — or manually by your team. Maintaining a clean suppression
          list protects your sender reputation with AWS SES.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"         value={counts.total} />
        <Stat label="Unsubscribes"  value={counts.unsubscribe} />
        <Stat label="Hard Bounces"  value={counts.bounce}     alert={counts.bounce > 0} />
        <Stat label="Complaints"    value={counts.complaint}  alert={counts.complaint > 0} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input placeholder="Search email…" leftIcon={<Search size={14} />}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select value={reason}
            onChange={(e) => setReason(e.target.value as SuppressionReason | '')}
            options={[
              { value: '', label: 'All reasons' },
              ...Object.entries(REASON_LABELS).map(([v, l]) => ({ value: v, label: l })),
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={13} />} onClick={() => setAddOpen(true)}>
            Add Suppression
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState icon={ShieldOff}
            title={search || reason ? 'No suppressions match' : 'No suppressed emails'}
            description={search || reason
              ? 'Try clearing filters.'
              : 'That\'s good — no one has unsubscribed, bounced, or complained yet.'}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left bg-bg-subtle">
                <th className="kicker px-6 py-3 font-normal">Email</th>
                <th className="kicker px-6 py-3 font-normal">Reason</th>
                <th className="kicker px-6 py-3 font-normal">Notes</th>
                <th className="kicker px-6 py-3 font-normal">Added</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const Icon = REASON_ICONS[s.reason];
                return (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 group">
                    <td className="px-6 py-3 font-mono text-xs text-text">{s.email}</td>
                    <td className="px-6 py-3">
                      <Badge tone={REASON_TONES[s.reason]}>
                        <Icon size={9} /> {REASON_LABELS[s.reason]}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-text-muted text-xs">
                      {s.notes || <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-6 py-3 text-text-dim text-xs tabular">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {canWrite && (
                        <button onClick={() => setRemoving(s)}
                          className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm opacity-0 group-hover:opacity-100">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kicker="Add Suppression"
        title="Manually suppress an email"
        description="The email will be added to the suppression list and excluded from all future campaigns."
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button form="sup-form" type="submit" loading={add.isPending} leftIcon={<ShieldOff size={13} />}>
              Add to Suppressions
            </Button>
          </>
        }
      >
        <form id="sup-form" onSubmit={onAdd} className="space-y-4 py-2">
          <Input label="Email" type="email" required
            placeholder="person@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <Textarea label="Notes" rows={3}
            placeholder="Why is this email being suppressed?"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
        loading={remove.isPending}
        title={`Remove ${removing?.email} from suppressions?`}
        confirmLabel="Remove suppression"
        description="The email will be eligible to receive campaigns again. Do not do this for hard bounces or complaint-based suppressions unless you've verified the situation has changed."
      />
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${alert ? 'text-warning' : 'text-text'}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
